/* The workspace's rules, kept apart from its rendering.
 *
 * Grouping, ordering, the two filter rows, the progress count and the keyboard
 * order are all decisions about data, so they are pure functions here rather
 * than logic buried in a component. There is no browser test runner in this
 * repository — ARCHITECTURE.md records why — so anything whose correctness is a
 * claim about behaviour has to be assertable without a DOM.
 * tools/workspace-check.tsx asserts against this module and against the markup
 * the components built on it produce. */

import { evidenceBadgeFor } from '../data/report';
import { PLAN_AREA_IDS, PLAN_AREA_LABELS } from '../data/planAreas';
import type {
  Disposition,
  EvidenceFilter,
  Flag,
  MutedChecks,
  PlanAreaId,
} from '../types';

/* ==========================================================================
   Filter row one — the decision
   --------------------------------------------------------------------------
   "Asked an SME" is a decision, so a finding carrying it is decided and drops
   out of "To decide". It has no filter of its own: the row is four options,
   and a referred finding is found under "All".
   ========================================================================== */

export type DecisionFilter = 'all' | 'to-decide' | 'included' | 'not-a-concern';

export const DECISION_FILTERS: { value: DecisionFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'to-decide', label: 'To decide' },
  { value: 'included', label: 'Included' },
  { value: 'not-a-concern', label: 'Not a concern' },
];

/* ==========================================================================
   Filter row two — the evidence
   ========================================================================== */

export const EVIDENCE_FILTERS: { value: EvidenceFilter; label: string }[] = [
  { value: 'all', label: 'Any evidence' },
  { value: 'arithmetic', label: 'Arithmetic' },
  { value: 'audit-precedent', label: 'Audit precedent' },
  { value: 'guidance-warning', label: 'Guidance warning' },
];

export function matchesDecision(
  filter: DecisionFilter,
  disposition: Disposition | undefined,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'to-decide':
      return disposition === undefined;
    case 'included':
      return disposition?.decision === 'include';
    case 'not-a-concern':
      return disposition?.decision === 'not-a-concern';
    default:
      return true;
  }
}

export function matchesEvidence(filter: EvidenceFilter, finding: Flag): boolean {
  return filter === 'all' || evidenceBadgeFor(finding).id === filter;
}

export function filterFindings(
  findings: Flag[],
  dispositions: Record<string, Disposition>,
  decision: DecisionFilter,
  evidence: EvidenceFilter,
): Flag[] {
  return findings.filter(
    (f) =>
      matchesDecision(decision, dispositions[f.id]) && matchesEvidence(evidence, f),
  );
}

/* ==========================================================================
   Grouping — plan area, ordered by count
   ========================================================================== */

export interface FindingGroup {
  area: PlanAreaId;
  label: string;
  /** Every finding in this area, whatever the filters. The heading's count. */
  total: number;
  /** The findings the filters leave. Never empty in a returned group. */
  findings: Flag[];
}

/**
 * The groups the rail draws: plan areas holding at least one finding after the
 * filters, ordered by how many findings the area holds in total, largest
 * first. Ties fall back to the canonical plan-area order, so the list does not
 * reshuffle itself between renders.
 */
export function groupByPlanArea(
  findings: Flag[],
  visible: Flag[] = findings,
): FindingGroup[] {
  const visibleIds = new Set(visible.map((f) => f.id));

  return PLAN_AREA_IDS.map((area) => {
    const all = findings.filter((f) => f.planArea === area);
    return {
      area,
      label: PLAN_AREA_LABELS[area],
      total: all.length,
      findings: all.filter((f) => visibleIds.has(f.id)),
    };
  })
    .filter((g) => g.findings.length > 0)
    .sort(
      (a, b) =>
        b.total - a.total ||
        PLAN_AREA_IDS.indexOf(a.area) - PLAN_AREA_IDS.indexOf(b.area),
    );
}

/**
 * Plan areas nothing was found in. Deliberately not affected by the filters:
 * this is a statement about the checks, not about what the reviewer is
 * currently looking at, and it must not appear to change when a filter is
 * pressed.
 */
export function emptyPlanAreas(findings: Flag[]): PlanAreaId[] {
  return PLAN_AREA_IDS.filter((area) => !findings.some((f) => f.planArea === area));
}

/**
 * "Nothing found in: O&M and budget · Data of record and disputes."
 *
 * Absence of a finding is information. It goes beneath the list rather than
 * appearing as an empty group, so it cannot be mistaken for a check that never
 * ran. Returns null when every area holds something.
 */
export function nothingFoundLine(findings: Flag[]): string | null {
  const empty = emptyPlanAreas(findings);
  if (empty.length === 0) return null;
  return `Nothing found in: ${empty.map((a) => PLAN_AREA_LABELS[a]).join(' · ')}.`;
}

/* ==========================================================================
   Progress
   ========================================================================== */

export interface Progress {
  decided: number;
  total: number;
  /** "6 of 10 decided". A count, never a percentage and never a score. */
  label: string;
  /** 0 to 1, for the width of the hairline rule. Never shown as a number. */
  fraction: number;
}

export function progressOf(
  findings: Flag[],
  dispositions: Record<string, Disposition>,
): Progress {
  const decided = findings.filter((f) => dispositions[f.id] !== undefined).length;
  const total = findings.length;
  return {
    decided,
    total,
    label: `${decided} of ${total} decided`,
    fraction: total === 0 ? 0 : decided / total,
  };
}

/** The short words a decided row carries. The filter row's own vocabulary. */
export function dispositionMark(disposition: Disposition): string {
  switch (disposition.decision) {
    case 'include':
      return 'Included';
    case 'not-a-concern':
      return 'Not a concern';
    case 'refer':
      return 'Asked an SME';
    default:
      return 'Decided';
  }
}

/* ==========================================================================
   Muted checks
   --------------------------------------------------------------------------
   A muted check still runs and its findings still exist. They are moved out of
   the plan-area groups into one collapsed group at the bottom of the list, so
   that nothing is hidden from a federal reviewer — only moved out of the way of
   the work they said they wanted to get on with.
   ========================================================================== */

export interface MutedPartition {
  /** The findings the plan-area groups are built from. */
  active: Flag[];
  /** The findings of muted checks, in the order they were found. */
  muted: Flag[];
}

export function partitionMuted(
  findings: Flag[],
  muted: MutedChecks,
): MutedPartition {
  return {
    active: findings.filter((f) => muted[f.failureModeId] === undefined),
    muted: findings.filter((f) => muted[f.failureModeId] !== undefined),
  };
}

/**
 * "Muted checks (2)" — the collapsed group's own heading.
 *
 * It carries its count in the summary rather than behind the disclosure,
 * because a count that only appears once the group is open is a count nobody
 * reads.
 */
export function mutedGroupLabel(count: number): string {
  return `Muted checks (${count})`;
}

/* ==========================================================================
   Keyboard order
   ========================================================================== */

/**
 * The rows as the eye meets them: group by group, in the drawn order, then the
 * checks that have not produced findings, which the rail draws beneath the plan
 * areas. j and k reach those too — a check waiting on an answer is work, and
 * work the keyboard cannot reach is work the keyboard user does not have.
 */
export function keyboardOrder(
  groups: FindingGroup[],
  pending: { id: string }[] = [],
  muted: { id: string }[] = [],
): string[] {
  return [
    ...groups.flatMap((g) => g.findings.map((f) => f.id)),
    ...pending.map((p) => p.id),
    // Only when the muted group is open. A collapsed group has no rows to
    // focus, and j would otherwise walk into something that is not on screen.
    ...muted.map((m) => m.id),
  ];
}

/**
 * Where j / k / ArrowDown / ArrowUp go from here.
 *
 * A selection that is not in the visible order — filtered out, or none yet —
 * starts at the first row going down and the last going up. The ends do not
 * wrap: a reviewer working a list top to bottom should be able to tell when
 * they have reached the bottom of it.
 */
export function nextFindingId(
  order: string[],
  current: string | null,
  delta: 1 | -1,
): string | null {
  if (order.length === 0) return null;
  const at = current === null ? -1 : order.indexOf(current);
  if (at === -1) return delta === 1 ? order[0] : order[order.length - 1];
  const next = at + delta;
  if (next < 0 || next >= order.length) return order[at];
  return order[next];
}

/** The keys the rail answers to: the arrows, and j/k for the same movement. */
export function navDeltaFor(key: string): 1 | -1 | null {
  if (key === 'ArrowDown' || key === 'j' || key === 'J') return 1;
  if (key === 'ArrowUp' || key === 'k' || key === 'K') return -1;
  return null;
}

/**
 * What the workspace opens on: the reviewer's last position if it still
 * exists, otherwise the first finding they have not decided, otherwise the
 * first finding. Resuming should land on work, not on something already done.
 */
export function initialSelection(
  findings: Flag[],
  dispositions: Record<string, Disposition>,
  remembered: string | null,
): string | null {
  const rememberedFinding = remembered
    ? findings.find((f) => f.id === remembered)
    : undefined;
  // Where they left off, but only if there is still work on it. A reviewer
  // whose last act was deciding the finding they were reading does not want to
  // come back to it; they want the next one. JOURNEYS.md: "Resuming drops them
  // at the first undecided finding."
  if (rememberedFinding && dispositions[rememberedFinding.id] === undefined) {
    return rememberedFinding.id;
  }
  const undecided = findings.find((f) => dispositions[f.id] === undefined);
  if (undecided) return undecided.id;
  // Everything is decided. Staying where they were beats jumping to the top.
  return rememberedFinding?.id ?? findings[0]?.id ?? null;
}
